import {
  ClientOrCache,
  dataIdFromObject,
  evictFieldIf,
} from '@sketch/graphql-cache'
import produce from 'immer'

import {
  GetProjectSharesDocument,
  GetProjectSharesQuery,
  GetProjectSharesQueryVariables,
  ShareListItemFragment,
  ShareListItemFragmentDoc,
  GetProjectsListQueryVariables,
  ProjectFragment,
  ProjectListCountsFragment,
  ProjectListCountsFragmentDoc,
  ProjectFragmentDoc,
  NestedProjectFragmentDoc,
  NestedProjectFragment,
  ApolloCache,
  GetTrashQuery,
  GetSharesQuery,
  GetProjectsListQuery,
  ProjectPreviewFragmentDoc,
  ProjectPreviewFragment,
} from '@sketch/gql-types'
import {
  errorPreventiveCacheRead,
  removeFromPaginated,
} from '../../utils/apolloCache'

// eslint-disable-next-line no-restricted-imports
import { SharesSearchParams } from '@sketch/gql-types/expansive'
import { removeFromStorageItemsList } from '../../storageItems'

type ProjectRecipeFunction = (cachedObject: GetProjectSharesQuery) => void

interface FilterArguments {
  projectId: string
  search?: string | null
  filters?: SharesSearchParams['filters']
}

const createProjectQuery = ({
  projectId,
  search = null,
  filters,
}: FilterArguments) => {
  return {
    query: GetProjectSharesDocument,
    variables: {
      shortId: projectId,
      after: null,
      search: {
        name: search,
        isCurrentVersionDownloadable: null,
        filters,
      } as const,
    },
  }
}

export const readProjectList = (
  cache: ClientOrCache,
  filterArguments: FilterArguments
) => {
  const cacheArguments = createProjectQuery(filterArguments)

  return errorPreventiveCacheRead<
    GetProjectSharesQuery,
    GetProjectSharesQueryVariables
  >(cache, cacheArguments)
}

export const updateProjectList = (
  cache: ClientOrCache,
  filterArguments: FilterArguments,
  recipe: ProjectRecipeFunction
) => {
  const cachedProject = readProjectList(cache, filterArguments)

  if (!cachedProject) {
    return
  }

  const project = produce(cachedProject, recipe)

  cache.writeQuery<GetProjectSharesQuery, GetProjectSharesQueryVariables>({
    ...createProjectQuery(filterArguments),
    data: project,
  })

  return project
}

const createShareFragmentQuery = (shareIdentifier: string) => {
  return {
    fragment: ShareListItemFragmentDoc,
    fragmentName: 'ShareListItem',
    id: dataIdFromObject({
      __typename: 'Share',
      identifier: shareIdentifier,
    }) as string,
  }
}

export const readShareWithProjectFragment = (
  cache: ClientOrCache,
  shareIdentifier: string
) => {
  const cacheArguments = createShareFragmentQuery(shareIdentifier)

  return cache.readFragment<ShareListItemFragment>(cacheArguments)
}

type ShareRecipeFunction = (cachedObject: ShareListItemFragment) => void

export const updateShareWithProjectFragment = (
  cache: ClientOrCache,
  shareIdentifier: string,
  recipe: ShareRecipeFunction
) => {
  const cachedShare = readShareWithProjectFragment(cache, shareIdentifier)

  if (!cachedShare) {
    return
  }

  const share = produce(cachedShare, recipe)

  cache.writeFragment({
    ...createShareFragmentQuery(shareIdentifier),
    data: share,
  })

  return share
}

export const deleteWorkspaceProject = (
  cache: ApolloCache<any>,
  workspaceId: string,
  projectId: string
) => {
  const targetProject = {
    __typename: 'Project',
    identifier: projectId,
  } satisfies Pick<ProjectFragment, 'identifier' | '__typename'>

  removeFromPaginated(cache, targetProject, key => key.includes('.projects'))

  removeFromStorageItemsList({ cache, target: targetProject })

  // Invalidate the workspace trash and the shares queries
  // we can have shares that belong to the deleted project that
  // will be deleted was well
  type TrashQueryField = keyof GetTrashQuery
  const trashFieldName: TrashQueryField = 'workspaceTrash'

  evictFieldIf<{ identifier?: string }>(
    cache,
    {
      id: 'ROOT_QUERY',
      fieldName: trashFieldName,
    },
    fieldArguments => fieldArguments?.identifier === workspaceId
  )

  type WorkspaceSharesField = keyof GetSharesQuery['workspace']
  const sharesFieldName: WorkspaceSharesField = 'shares'

  cache.evict({
    id: cache.identify({
      __typename: 'Workspace',
      identifier: workspaceId,
    }),
    fieldName: sharesFieldName,
  })
  cache.gc()
}

export const restoreWorkspaceProject = (
  cache: ApolloCache<any>,
  workspaceId: string,
  projectId: string,
  archived: boolean
) => {
  // Remove the project from the trash query
  removeFromPaginated(
    cache,
    { __typename: 'Project', identifier: projectId },
    key => key.includes('workspaceTrash')
  )

  // Invalidate the workspace trash and the shares queries
  // we can have shares that belong to the deleted project that
  // will be deleted was well
  type TrashQueryField = keyof GetTrashQuery
  const trashFieldName: TrashQueryField = 'workspaceTrash'
  evictFieldIf<{ identifier?: string }>(
    cache,
    {
      id: 'ROOT_QUERY',
      fieldName: trashFieldName,
    },
    fieldArguments => fieldArguments?.identifier === workspaceId
  )

  type WorkspaceSharesField = keyof GetSharesQuery['workspace']
  const sharesFieldName: WorkspaceSharesField = 'shares'
  const workspaceCacheId = cache.identify({
    __typename: 'Workspace',
    identifier: workspaceId,
  })
  cache.evict({
    id: workspaceCacheId,
    fieldName: sharesFieldName,
  })

  type WorkspaceProjectsField = keyof GetProjectsListQuery['workspace']
  const projectsFieldName: WorkspaceProjectsField = 'projects'
  if (archived) {
    evictFieldIf<{ archived?: GetProjectsListQueryVariables['archived'] }>(
      cache,
      { id: workspaceCacheId, fieldName: projectsFieldName },
      fieldArguments => fieldArguments?.archived === 'ARCHIVED'
    )
  }

  cache.gc()
}

type ProjectCountsRecipeFunction = (
  cachedObject: ProjectListCountsFragment
) => void

export const updateProjectListCounts = (
  cache: ClientOrCache,
  projectId: string,
  recipe: ProjectCountsRecipeFunction
) => {
  const cacheArgs = {
    id: dataIdFromObject({
      __typename: 'Project',
      identifier: projectId,
    }) as string,
    fragment: ProjectListCountsFragmentDoc,
    fragmentName: 'ProjectListCounts',
  }
  const cachedProject = cache.readFragment<ProjectListCountsFragment>(cacheArgs)

  if (!cachedProject) return

  const updated = produce(cachedProject, recipe)

  cache.writeFragment<ProjectListCountsFragment>({
    ...cacheArgs,
    data: updated,
  })

  return updated
}

type ProjectPreviewsAndCountsRecipeFunction = (
  cachedObject: ProjectPreviewFragment
) => void

export const updateProjectListPreviewsAndCounts = (
  cache: ClientOrCache,
  projectId: string,
  recipe: ProjectPreviewsAndCountsRecipeFunction
) => {
  const cacheArgs = {
    id: dataIdFromObject({
      __typename: 'Project',
      identifier: projectId,
    }) as string,
    fragment: ProjectPreviewFragmentDoc,
    fragmentName: 'ProjectPreview',
  }
  const cachedProject = cache.readFragment<ProjectPreviewFragment>(cacheArgs)

  if (!cachedProject) return

  const updated = produce(cachedProject, recipe)

  cache.writeFragment<ProjectPreviewFragment>({
    ...cacheArgs,
    data: updated,
  })

  return updated
}

export const readProjectFromCache = (
  cache: ClientOrCache,
  identifier: string
) => {
  try {
    return (
      cache.readFragment<ProjectFragment>({
        fragment: ProjectFragmentDoc,
        fragmentName: 'Project',
        id: dataIdFromObject({
          __typename: 'Project',
          identifier,
        })!,
      }) || undefined
    )
  } catch (e) {
    // This is a preventive way of returning an error to the UI
    // If this returns an error we simulate that the object does not exist
    return undefined
  }
}

export const readNestedProjectFromCache = (
  cache: ClientOrCache,
  identifier: string
) => {
  try {
    return (
      cache.readFragment<NestedProjectFragment>({
        fragment: NestedProjectFragmentDoc,
        fragmentName: 'NestedProject',
        id: dataIdFromObject({
          __typename: 'Project',
          identifier,
        })!,
      }) || undefined
    )
  } catch (e) {
    // This is a preventive way of returning an error to the UI
    // If this returns an error we simulate that the object does not exist
    return undefined
  }
}

interface InvalidateProjectSharesProps {
  cache: ApolloCache<any>
  projectIdentifier: string
}

export const invalidateProjectShares = ({
  cache,
  projectIdentifier,
}: InvalidateProjectSharesProps) => {
  const id = cache.identify({
    __typename: 'Project',
    identifier: projectIdentifier,
  })!

  type Fields = keyof GetProjectSharesQuery['project']
  const fieldName: Fields = 'shares'

  cache.evict({ fieldName, id })
  cache.gc()
}
